import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Exact, type CategoryDto, type ItemFormOptions, type ItemListDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api, ApiFailure } from "../../lib/api";
import { errorText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { ListView, type Column } from "../../components/ListView";
import { Confirm } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { LockedButton, lockTag } from "../settings/common";
import { pct, qty } from "./common";
import { downloadExport } from "./itemSheet";
import { saveBlob } from "../../print/pdf";

// Items › All Items, ported from the prototype (erp-items.js "/items/allItems"). Audited columns: item name,
// category, code, discount, quantity and minimum quantity, plus the barcode search box above the list. Quantity is
// the stock on hand in base units in the warehouses in scope; it shows in red below the minimum quantity.

export function Items() {
  const { t, L, lang } = useLang();
  const s = useSession();
  const toast = useToast();
  const nav = useNavigate();
  const [rows, setRows] = useState<ItemListDto[] | null>(null);
  const [cats, setCats] = useState<CategoryDto[]>([]);
  const [kinds, setKinds] = useState<ItemFormOptions["itemKinds"]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [deleting, setDeleting] = useState<ItemListDto | null>(null);
  const [scan, setScan] = useState("");

  const load = useCallback(async () => {
    setFailed(null);
    const p = new URLSearchParams();
    if (filters.name) p.set("q", filters.name);
    if (filters.cat) p.set("categoryId", filters.cat);
    if (filters.code) p.set("code", filters.code);
    if (filters.kind) p.set("itemKindId", filters.kind);
    try { setRows(await api<ItemListDto[]>("GET", `/items${p.size ? "?" + p : ""}`)); }
    catch (e) { setFailed(errorText(t, e)); }
  }, [filters.name, filters.cat, filters.code, filters.kind, t]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { api<ItemFormOptions>("GET", "/items/form-options").then((o) => { setCats(o.categories); setKinds(o.itemKinds); }, () => undefined); }, []);

  async function findBarcode() {
    const code = scan.trim();
    if (!code) return;
    try { const hit = await api<{ itemId: string }>("GET", `/items/barcode/${encodeURIComponent(code)}`); nav(`/items/show/${hit.itemId}`); }
    catch (e) { toast(e instanceof ApiFailure && e.status === 404 ? t("barcodeNotFound", { x: code }) : errorText(t, e)); }
  }
  async function doDelete(it: ItemListDto) {
    setDeleting(null);
    try { await api("DELETE", `/items/${it.id}`); toast(t("deletedOk", { x: L(it) })); }
    catch (e) { toast(errorText(t, e)); }
    await load();
  }

  const catName = (it: ItemListDto) => L({ nameEn: it.categoryNameEn, nameAr: it.categoryNameAr });
  const columns: Column<ItemListDto>[] = [
    { k: "name", label: t("itemName"), csv: (it) => L(it), cell: (it) => (
      <div className="item-cell">
        {it.imageAt ? <img src={`/api/items/${it.id}/image?v=${encodeURIComponent(it.imageAt)}`} alt="" loading="lazy" data-testid="item-thumb" /> : <span className="it-ico" data-testid="item-thumb-none"><Icon name="box" /></span>}
        <a className="cell-name strong" href={`/items/show/${it.id}`} onClick={(e) => { e.preventDefault(); nav(`/items/show/${it.id}`); }}>{L(it)}</a>
      </div>
    ) },
    { k: "cat", label: t("categoryName"), cell: catName, csv: catName },
    { k: "code", label: t("itemCode"), cell: (it) => it.code ? <span className="num" dir="ltr">{it.code}</span> : <span className="muted">–</span>, csv: (it) => it.code },
    { k: "disc", label: t("discount"), end: true, cell: (it) => pct(it.discount), csv: (it) => it.discount },
    { k: "qty", label: t("qty"), end: true, cell: (it) => (it.stockQty === null ? <span className="muted">–</span> : <span className={/[1-9]/.test(it.minQty) && Exact.cmp(Exact.dec(it.stockQty), Exact.dec(it.minQty)) < 0 ? "over" : ""} data-testid="item-qty">{qty(it.stockQty)}</span>), csv: (it) => it.stockQty ?? "" },
    { k: "min", label: t("minQty"), end: true, cell: (it) => qty(it.minQty), csv: (it) => it.minQty }
  ];
  // The simplified sheet (request of 31 Oct 2026): every item with selling price, cost, type, both category names and
  // hidden ids, in the import template's columns, so the file can be edited and imported again (itemSheet.ts).
  const exportBtn = <button className="btn" title={t("itemsExportHint")} onClick={() => void downloadExport(lang, (m, u) => api(m, u), saveBlob).catch((e) => toast(errorText(t, e)))} data-testid="items-export-excel"><Icon name="download" />{t("itemsExportExcel")}</button>;
  // The import adds items (itemAdd) or updates them (itemUpdate): either opens it (review of 31 Oct 2026, item 3).
  const importBtn = s.can("itemAdd") || s.can("itemUpdate") ? <button className="btn" onClick={() => nav("/items/import")} data-testid="items-import"><Icon name="upload" />{t("impTitle")}</button> : null;
  const newBtn = s.can("itemAdd") ? <>{exportBtn}{importBtn}<button className="btn btn-primary" onClick={() => nav("/items/create")}><Icon name="plus" />{t("newItem")}</button></>
    : <>{exportBtn}{importBtn}<LockedButton label={t("newItem")} title={t("noPermissionFor", { p: "itemAdd" })} /></>;

  return (
    <Shell navKey="/items/allItems">
      <PageHead crumbs={[[t("home"), "/home"], [t("items")], [t("allItems")]]} title={t("allItems")} />
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="ltb-above" style={{ paddingBottom: 14 }}>
          <form className="barcode input-group" onSubmit={(e) => { e.preventDefault(); void findBarcode(); }}>
            <span className="lead"><Icon name="barcode" /></span>
            <input className="control num" style={{ textAlign: "start" }} dir="ltr" type="search" placeholder={t("searchBarcode")} aria-label={t("searchBarcode")}
              value={scan} onChange={(e) => setScan(e.target.value)} data-testid="barcode-search" />
          </form>
          <span className="hint">{t("barcodeHintItems")}</span>
        </div>
      </div>
      <ListView rows={rows} error={failed} columns={columns} file="items" testId="items-table" onRefresh={() => void load()}
        search={[
          { k: "name", label: t("itemName"), type: "text" },
          { k: "cat", label: t("categoryName"), type: "select", options: cats.map((c) => [c.id, L(c)]) },
          { k: "code", label: t("itemCode"), type: "text" },
          { k: "kind", label: t("itemKind"), type: "select", options: kinds.map((k) => [k.id, L(k)]) }
        ]} filters={filters} onFilters={setFilters}
        quick={(it) => `${it.nameEn} ${it.nameAr} ${it.code} ${it.barcodes.join(" ")}`} newButton={newBtn}
        empty={{ icon: "box", title: t("noResults"), body: t("noResultsBody") }}
        actions={(it) => [
          { label: t("view"), icon: "eye", run: () => nav(`/items/show/${it.id}`) },
          { label: t("editItem"), icon: "edit", run: () => nav(`/items/edit/${it.id}`), ...(s.can("itemUpdate") ? {} : lockTag(t)) },
          { label: "", sep: true },
          { label: t("delete"), icon: "trash", danger: true, run: () => setDeleting(it), ...(s.can("itemDelete") ? {} : lockTag(t)) }
        ]} />
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => void doDelete(deleting)} />}
    </Shell>
  );
}
